import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Switch } from "../ui/switch";
import { Field } from "../../pages/Checkout";

function TierRows({ table, onChange }) {
  const upd = (i, k, v) => onChange(table.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  return (
    <div className="space-y-2" data-testid="shipping-table">
      {table.map((t, i) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <span className="shrink-0 text-white/60">Up to</span>
          <input type="number" step="0.1" min="0.1" className="field min-w-0 flex-1" value={t.max_lb} onChange={(e) => upd(i, "max_lb", e.target.value)} data-testid={`shipping-tier-weight-${i}`} />
          <span className="text-white/60">lb</span>
          <span className="ml-1 text-white/60">$</span>
          <input type="number" step="0.01" min="0" className="field min-w-0 flex-1" value={t.price} onChange={(e) => upd(i, "price", e.target.value)} data-testid={`shipping-tier-price-${i}`} />
          <button type="button" onClick={() => onChange(table.filter((_, j) => j !== i))} className="rounded-full p-2 hover:text-[var(--pink)]" aria-label="Remove tier" data-testid={`shipping-tier-remove-${i}`}><X className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...table, { max_lb: "", price: "" }])} className="inline-flex items-center gap-1 text-sm font-bold text-[var(--blue-soft)] hover:text-[var(--pink)]" data-testid="shipping-tier-add"><Plus className="h-4 w-4" /> Add weight tier</button>
    </div>
  );
}

export default function AdminShipping() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/shipping").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="text-white/60">Loading shipping settings...</p>;

  const setO = (k) => (e) => setS({ ...s, origin: { ...s.origin, [k]: k === "state" ? e.target.value.toUpperCase() : e.target.value } });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const table = s.table.map((t) => ({ max_lb: parseFloat(t.max_lb), price: parseFloat(t.price) }));
      const { data } = await api.put("/admin/shipping", { origin: s.origin, table_label: s.table_label, table, use_live_rates: s.use_live_rates });
      setS(data);
      toast.success("Shipping settings saved");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <form onSubmit={save} className="grid min-w-0 gap-6 lg:grid-cols-2" data-testid="admin-shipping">
      <div className="panel min-w-0 space-y-4 p-5 sm:p-6">
        <p className="font-display text-xl">Ship from</p>
        <Field label="Street (optional, improves rate accuracy)"><input className="field" value={s.origin.street1 || ""} onChange={setO("street1")} data-testid="shipping-origin-street-input" /></Field>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-[1fr_80px_110px]">
          <div className="col-span-2 sm:col-span-1"><Field label="City"><input className="field" value={s.origin.city} onChange={setO("city")} data-testid="shipping-origin-city-input" /></Field></div>
          <Field label="State"><input maxLength={2} className="field uppercase" value={s.origin.state} onChange={setO("state")} data-testid="shipping-origin-state-input" /></Field>
          <Field label="ZIP"><input className="field" value={s.origin.zip} onChange={setO("zip")} data-testid="shipping-origin-zip-input" /></Field>
        </div>
        <div className="rounded-2xl bg-white/5 p-4 text-sm" data-testid="shipping-live-status">
          <label className="flex items-center gap-3 font-bold">
            <Switch checked={s.use_live_rates} onCheckedChange={(v) => setS({ ...s, use_live_rates: v })} data-testid="shipping-live-switch" /> Use live USPS / UPS rates
          </label>
          <p className="mt-2 text-white/60">{s.live_key_set ? "Your shipping account is connected. If live rates are unavailable, your price table is used." : "Shipping account not connected yet, so your price table is used for now."}</p>
        </div>
        <p className="text-xs text-white/50">Only products marked "Can be mailed" are charged postage. Set each product's weight in the Products tab.</p>
      </div>
      <div className="panel min-w-0 space-y-4 p-5 sm:p-6">
        <p className="font-display text-xl">My shipping price table</p>
        <Field label="Name shown at checkout"><input className="field" value={s.table_label} onChange={(e) => setS({ ...s, table_label: e.target.value })} data-testid="shipping-table-label-input" /></Field>
        <TierRows table={s.table} onChange={(table) => setS({ ...s, table })} />
        <button disabled={busy} className="btn-candy w-full" data-testid="shipping-save-button">{busy ? "Saving..." : "Save shipping settings"}</button>
      </div>
    </form>
  );
}
